import { Injectable } from '@angular/core';
import { ArticleUsageVO } from '../model/vo/article.vo';
import { ArticleUsageUtils } from '../util/article-usage-utils';
import { DefaultReplaceArticleManager } from '@project/components/article/manager/default-replace-article.manager';
import { ArticleManager } from '../manager/article.manager';
import { ProjectModel } from '@project/model/project.model';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { lastValueFrom } from 'rxjs';
import { unique } from '@shared/util/array-utils';

const peAccessoryArticleIds = [ 65525, 65526 ];

const articleToPeArticle: Record<number, number> = {
  [65504]: 66160,
  [65505]: 66161,
  [65506]: 66028,
  [65507]: 66159,
};

const peArticleToArticle: Record<number, number> = Object.fromEntries(
  Object.entries(articleToPeArticle).map(([ key, value ]) => [ value, Number(key) ]),
);

@Injectable({
  providedIn: 'root',
})
export class ChangeLtlArticleService {

  constructor(
    private replaceArticleManager: DefaultReplaceArticleManager,
    private articleManager: ArticleManager,
    private projectModel: ProjectModel,
  ) {}

  public async articlesAdded(articleUsages: ArticleUsageVO[]): Promise<void> {
    const parentUsages = this.getAffectedParents(articleUsages);
    // We have to filter unique parents because it's possible to attach two accessories to the same parent at once.
    for (const parentUsage of parentUsages.filter(unique)) {
      await this.replaceArticleUsageIfPossible(parentUsage, articleToPeArticle);
    }
  }

  public async articlesRemoved(articleUsages: ArticleUsageVO[]) {
    const parentUsages = this.getAffectedParents(articleUsages);
    // We have to filter unique parents because it's possible to remove two accessories from the same parent at once.
    for (const parentUsage of parentUsages.filter(unique)) {
      const childUsages = this.getRelevantChildren(parentUsage);
      if (childUsages.length === 0) {
        await this.replaceArticleUsageIfPossible(parentUsage, peArticleToArticle);
      }
    }
  }

  private async replaceArticleUsageIfPossible(articleUsage: ArticleUsageVO, articleIdMap: Record<number, number>): Promise<void> {
    const replaceId: number = articleIdMap[articleUsage.articleId];
    if (replaceId) {
      const article = await lastValueFrom(this.articleManager.loadFullArticle(replaceId));
      await this.replaceArticleManager.replaceArticlesByUsageIds([ articleUsage.id ], article);
    }
  }

  private getAffectedParents(articleUsages: ArticleUsageVO[]) {
    const parentUsages: ArticleUsageVO[] = [];
    for (const articleUsage of articleUsages) {
      if (peAccessoryArticleIds.includes(articleUsage.articleId)) {
        const parentConnectionAreaUsages = ArticleUsageUtils.backwardConnectionAreaUsages(articleUsage);
        for (const parentConnectionAreaUsage of parentConnectionAreaUsages) {
          const projectArticleUsage = ProjectUtils.articleUsages(this.projectModel.currentProject).find((projectUsage) => projectUsage.id === parentConnectionAreaUsage.connection.acceptingAreaId.articleUsageId);
          if (projectArticleUsage) {
            parentUsages.push(projectArticleUsage);
          }
        }
      }
    }
    return parentUsages;
  }

  private getRelevantChildren(articleUsage: ArticleUsageVO): ArticleUsageVO[] {
    const childUsages: ArticleUsageVO[] = [];
    const projectArticleUsages = ProjectUtils.articleUsages(this.projectModel.currentProject);
    const childConnectionAreaUsages = ArticleUsageUtils.forwardConnectionAreaUsages(articleUsage);
    const givingIdentities = childConnectionAreaUsages.flatMap((childConnectionAreaUsage) => childConnectionAreaUsage.connection.givingAreaIds);
    for (const givingIdentity of givingIdentities) {
      const projectArticleUsage = projectArticleUsages.find((projectUsage) => projectUsage.id === givingIdentity.articleUsageId);
      if (projectArticleUsage && peAccessoryArticleIds.includes(projectArticleUsage.articleId)) {
        childUsages.push(projectArticleUsage);
      }
    }
    return childUsages;
  }
}
